import {
    Checkbox,
    createStyles,
    FormControl,
    FormControlLabel,
    Input,
    InputLabel,
    MenuItem,
    Select,
    WithStyles,
    withStyles,
} from '@material-ui/core';
import { inject } from 'mobx-react';
import * as React from 'react';
import ESDataTableWithHeader from 'src/common/ESDataTableWithHeader';
import IDataTableManager from 'src/common/IDataTableManager';
import { DefaultAssessment, IAssessment } from 'src/Definitions';
import { ObserverDto } from 'src/generated_client';
import { IStores } from 'src/Stores';
import AssessmentDialog from './AssessmentDialog';
import CompleteAssessmentDialog from './CompleteAssessmentDialog';

const styles = createStyles({
    checkBoxLabel: {
        paddingLeft: '20px',
    },
    formControl: {
        display: 'flex',
        wrap: 'nowrap',
    },
});

interface IAssessmentsProps {
    dataTableManager?: IDataTableManager<IAssessment>;
    disableAssessment?: (assessment: IAssessment) => void;
    getAllRegionsAndObservers?: () => void;
    getColumnSettingsAndAssessments?: () => void;
    includeCompleted?: boolean;
    includeEnded?: boolean;
    isConfirmDialogOpen?: boolean;
    isModalOpen?: boolean;
    openDialogWithAssessment?: (assessment: IAssessment) => void;
    recoverAssessment?: (assessment: IAssessment) => void;
    allRegions?: string[];
    selectedRegions?: string[];
    setSelectedRegions?: (regions: string[]) => void;
    allObservers?: ObserverDto[];
    selectedAssessment?: IAssessment;
    selectedObservers?: string[];
    setIncludeCompleted?: (show: boolean) => void;
    setIncludeEnded?: (show: boolean) => void;
    setSelectedObservers?: (observers: string[]) => void;
    rerouteToPath?: (path: string) => void;
}

class Assessments extends React.Component<IAssessmentsProps & WithStyles<typeof styles>> {
    public componentDidMount() {
        this.props.getColumnSettingsAndAssessments!();
    }

    public observerFilterRenderValue = (items: ObserverDto[]) => (observerIds: string[]) => {
        return items
            .filter((i) => observerIds.includes(i.observerId || ''))
            .map(
                (observer, index) =>
                    `${index ? ', ' : ''} ${observer.observerName}, ${observer.observerEmail}`
            );
    };

    public regionFilterRenderValue = (items: string[]) => (selectedItems: string[]) => {
        return items
            .filter((selected) => selectedItems.includes(selected))
            .map((name, index) => `${index ? ', ' : ''} ${name}`);
    };

    public render() {
        const {
            allObservers,
            allRegions,
            classes,
            setSelectedObservers,
            setSelectedRegions,
            selectedObservers,
            selectedRegions,
        } = this.props;

        function handleObserverSelectChange(event: React.ChangeEvent<{ value: unknown }>) {
            const newSelectedObservers = event.target.value as string[];
            setSelectedObservers!(newSelectedObservers);
        }

        function handleRegionSelectChange(event: React.ChangeEvent<{ value: unknown }>) {
            const newSelectedRegions = event.target.value as string[];
            setSelectedRegions!(newSelectedRegions);
        }

        const filterByObserver = (
            <FormControl className={classes.formControl}>
                <InputLabel>Observers</InputLabel>
                <Select
                    data-cy="observersFilter"
                    multiple
                    value={selectedObservers || []}
                    onChange={handleObserverSelectChange}
                    input={<Input id="select-multiple-checkbox" />}
                    renderValue={this.observerFilterRenderValue(allObservers || [])}
                >
                    {(allObservers || []).map((dto: ObserverDto, index: number) => (
                        <MenuItem key={index} value={dto.observerId}>
                            <Checkbox
                                checked={(selectedObservers as any).includes(dto.observerId)}
                            />
                            {dto.observerName + ', ' + dto.observerEmail}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );

        const filterByRegion = (
            <FormControl className={classes.formControl}>
                <InputLabel>Regions</InputLabel>
                <Select
                    data-cy="regionsFilter"
                    multiple
                    value={selectedRegions || []}
                    onChange={handleRegionSelectChange}
                    input={<Input id="select-multiple" />}
                    renderValue={this.regionFilterRenderValue(allRegions || [])}
                >
                    {(allRegions || []).map((name: string, index: number) => (
                        <MenuItem key={index} value={name}>
                            <Checkbox checked={((selectedRegions || []) as any).includes(name)} />
                            {name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );

        const showCompleted = () => {
            return (
                <FormControlLabel
                    className={classes.checkBoxLabel}
                    control={
                        <Checkbox
                            data-cy="showCompleted"
                            checked={
                                this.props.includeCompleted
                                    ? this.props.includeCompleted
                                    : this.props.dataTableManager!.tableOptions.showCompleted
                            }
                            color="primary"
                            name="showCompleted"
                            onChange={this.onShowCompletedChanged}
                            value="shouldShowCompleted"
                        />
                    }
                    label="Include Completed"
                />
            );
        };

        const showVisitEnded = () => {
            return (
                <FormControlLabel
                    className={classes.checkBoxLabel}
                    control={
                        <Checkbox
                            data-cy="showVisitEnded"
                            checked={
                                this.props.includeEnded
                                    ? this.props.includeEnded
                                    : this.props.dataTableManager!.tableOptions.showVisitEnded
                            }
                            color="primary"
                            name="showVisitEnded"
                            onChange={this.onShowVisitEndedChanged}
                            value="shouldShowVisitEnded"
                        />
                    }
                    label="Include Ended Visits"
                />
            );
        };

        const dialogKey = this.props.selectedAssessment ? this.props.selectedAssessment.id : '-1';
        const assessmentDialog = this.props.isModalOpen && <AssessmentDialog key={dialogKey} />;
        const completeAssessmentDialog = this.props.isConfirmDialogOpen && (
            <CompleteAssessmentDialog key={dialogKey} open={true} />
        );
        return (
            <>
                <ESDataTableWithHeader
                    dataTableManager={this.props.dataTableManager!}
                    enableEdit={true}
                    enableReorderColumns={true}
                    enableSearch={true}
                    additionalDropDownFilters={[filterByRegion, filterByObserver]}
                    additionalToggleFilters={[showCompleted, showVisitEnded]}
                    enableToggleColumns={true}
                    onRowClick={this.onRowClick}
                    openDialog={this.openDialog(DefaultAssessment(true))}
                    title="Assessments"
                />
                {assessmentDialog}
                {completeAssessmentDialog}
            </>
        );
    }

    private onShowCompletedChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.checked;
        this.props.dataTableManager!.updateTableOptions({
            filter: this.props.dataTableManager!.tableOptions.filter,
            page: 1,
            showCompleted: value,
            showVisitEnded: this.props.dataTableManager!.tableOptions.showVisitEnded,
        });
        this.props.setIncludeCompleted!(value);
    };

    private onShowVisitEndedChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.checked;
        this.props.dataTableManager!.updateTableOptions({
            filter: this.props.dataTableManager!.tableOptions.filter,
            page: 1,
            showCompleted: this.props.dataTableManager!.tableOptions.showCompleted,
            showVisitEnded: value,
        });
        this.props.setIncludeEnded!(value);
    };

    public openDialog = (assessment: IAssessment) => () => {
        this.props.openDialogWithAssessment!(assessment);
    };

    public onRowClick = (
        _event: React.MouseEvent<Element>,
        assessment: IAssessment,
        _idx: number
    ) => {
        this.props.rerouteToPath!('/chevroneditor/' + assessment.id);
    };
}

const InjectedAssessments = inject<IStores, IAssessmentsProps, Partial<IAssessmentsProps>, any>(
    (stores: IStores) => ({
        allObservers: stores.assessments.allObservers,
        allRegions: stores.assessments.allRegions,
        dataTableManager: stores.assessments.dataTableStore,
        disableAssessment: stores.assessments.disableAssessment,
        getColumnSettingsAndAssessments: stores.assessments.getColumnSettingsAndAssessments,
        includeCompleted: stores.assessments.includeCompleted,
        includeEnded: stores.assessments.includeEnded,
        isConfirmDialogOpen: stores.assessments.isConfirmDialogOpen,
        isModalOpen: stores.assessments.isModalOpen,
        openDialogWithAssessment: stores.assessments.openDialogWithAssessment,
        recoverAssessment: stores.assessments.recoverAssessment,
        rerouteToPath: stores.global.rerouteToPath,
        selectedAssessment: stores.assessments.selectedAssessment,
        selectedObservers: stores.assessments.selectedObservers,
        selectedRegions: stores.assessments.selectedRegions,
        setIncludeCompleted: stores.assessments.setIncludeCompleted,
        setIncludeEnded: stores.assessments.setIncludeEnded,
        setSelectedObservers: stores.assessments.setSelectedObservers,
        setSelectedRegions: stores.assessments.setSelectedRegions,
    })
)(withStyles(styles)(Assessments));

export default InjectedAssessments;



// WEBPACK FOOTER //
// ./src/assessments/Assessments.tsx